import { useMemo } from "react";
import dayjs from "dayjs";
import { oids } from "@/configs/Settings";
import { isQuestionnaireTask } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import { INVITE_TASK_STATUSES } from "src-next/modules/teleconsultoria/invites/constants";
import {
  isInviteExpired,
  isTeleconsultoriaInviteTask,
} from "src-next/modules/teleconsultoria/invites/helpers/inviteTaskHelpers";

interface UseFilteredNotificationsParams {
  allTasks: any[];
  user?: { username?: string };
  access?: { type?: string | null; organizationId?: string };
}

/**
 * Hook compartilhado para filtrar e ordenar notificações (tarefas FHIR Task).
 * Reutilizado por HeaderNotification e DashboardNotifications para manter
 * consistência nas regras de exibição.
 *
 * Regras:
 * - Filtra por task.for conforme tipo de acesso (patient vs professional)
 * - Profissional: inclui tasks com `for` = Patient quando for task de questionário
 *   enviada por ele (requester = PractitionerRole da sessão), para ver respostas.
 * - Mantém apenas status: requested, in-progress, completed
 * - Convite de teleconsultoria (aditivo): só para o próprio convidado
 *   (`for` = PractitionerRole da sessão), aceita os status do convite
 *   (aceito/recusado/cancelado) e, expirado, não conta como não lido.
 * - Janela de 30 dias (authoredOn ou meta.lastUpdated)
 * - Ordena por data decrescente (mais recente primeiro)
 */
export function useFilteredNotifications({
  allTasks,
  user,
  access,
}: UseFilteredNotificationsParams) {
  const { filteredTasks, unreadCount } = useMemo(() => {
    const validStatuses = new Set(["requested", "in-progress", "completed"]);
    const thirtyDaysAgo = dayjs().subtract(30, "days");
    const arr = Array.isArray(allTasks) ? allTasks : [];
    const myPatientReference = `Patient/${oids.cpf}-${user?.username}`;
    const myOrganizationReference = access?.organizationId
      ? `Organization/${access.organizationId}`
      : "";
    const practitionerRoleReference =
      access?.organizationId && user?.username
        ? `PractitionerRole/${access.organizationId}-${oids.cpf}-${user.username}`
        : "";

    const byFor = arr.filter((task: any) => {
      const forRef: string | undefined = task?.for?.reference;
      if (!forRef) return false;

      if (access?.type === "patient") {
        return forRef === myPatientReference;
      }
      if (access?.type === "professional") {
        if (isTeleconsultoriaInviteTask(task)) {
          return !!practitionerRoleReference && forRef === practitionerRoleReference;
        }
        if (!forRef.startsWith("Patient/")) return true;
        // Quando `for` é um Patient, só mostramos se for task de questionário.
        // Regra antiga (mantida): task enviada por este profissional (requester = PractitionerRole da sessão).
        // Regra nova (aditiva): task de questionário cuja owner seja a Organization selecionada.
        if (!isQuestionnaireTask(task)) return false;
        const matchesRequester =
          !!practitionerRoleReference &&
          task?.requester?.reference === practitionerRoleReference;
        const matchesOwnerOrg =
          !!myOrganizationReference &&
          task?.owner?.reference === myOrganizationReference;
        return matchesRequester || matchesOwnerOrg;
      }
      return true;
    });

    const filtered = byFor.filter((task: any) => {
      const statusOk =
        validStatuses.has(task?.status) ||
        (isTeleconsultoriaInviteTask(task) &&
          INVITE_TASK_STATUSES.has(task?.status));
      const taskDate = dayjs(task?.authoredOn || task?.meta?.lastUpdated);
      if (!taskDate.isValid()) return statusOk;
      return statusOk && taskDate.isAfter(thirtyDaysAgo);
    });

    const sorted = filtered.sort((a: any, b: any) => {
      const dateA = dayjs(a?.authoredOn || a?.meta?.lastUpdated);
      const dateB = dayjs(b?.authoredOn || b?.meta?.lastUpdated);
      return dateB.valueOf() - dateA.valueOf();
    });

    const unread = sorted.filter(
      (t: any) =>
        t?.status === "requested" &&
        !(isTeleconsultoriaInviteTask(t) && isInviteExpired(t)),
    ).length;

    return { filteredTasks: sorted, unreadCount: unread };
  }, [allTasks, access?.organizationId, access?.type, user?.username]);

  return { filteredTasks, unreadCount };
}
